import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Divider,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Send,
  Phone,
  WhatsApp,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import notificationService from '../services/notificationService';

interface ServiceFormProps {
  selectedService: string;
  onClose: () => void;
}

interface FormData {
  name: string;
  phone: string;
  address: string;
  projectType: string;
  budget: string;
  timeline: string;
  additionalRequirements: string;
  preferredContact: string[];
  urgency: string;
  message: string;
}

const ServiceForm: React.FC<ServiceFormProps> = ({ selectedService, onClose }) => {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    phone: '',
    address: '',
    projectType: '',
    budget: '',
    timeline: '',
    additionalRequirements: '',
    preferredContact: [],
    urgency: '',
    message: '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleInputChange = (field: keyof FormData) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | any
  ) => {
    const value = event.target.type === 'checkbox' 
      ? event.target.checked 
        ? [...formData.preferredContact, event.target.value]
        : formData.preferredContact.filter(item => item !== event.target.value)
      : event.target.value;
    
    setFormData({ ...formData, [field]: value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      // Prepare WhatsApp message
      const message = `New Service Request:

Service: ${selectedService}
Name: ${formData.name}
Phone: ${formData.phone}
Address: ${formData.address}
Project Type: ${formData.projectType}
Budget: ${formData.budget}
Timeline: ${formData.timeline}
Urgency: ${formData.urgency}
Additional Requirements: ${formData.additionalRequirements}
Message: ${formData.message}

Please contact the customer for more details.`;

      // Send to both numbers
      const phoneNumbers = ['8074635096', '9972555661'];
      phoneNumbers.forEach((phone, index) => {
        setTimeout(() => {
          window.open(`https://wa.me/91${phone}?text=${encodeURIComponent(message)}`);
        }, index * 1000); // Delay between messages
      });

      setSubmitStatus('success');
      
      // Reset form after success
      setTimeout(() => {
        onClose();
      }, 3000);

    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const isFormValid = formData.name && formData.phone && formData.address;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.3 }}
    >
      <Card sx={{ maxWidth: 600, mx: 'auto' }}>
        <CardContent sx={{ p: 4 }}>
          <Typography variant="h5" component="div" gutterBottom fontWeight="bold" textAlign="center">
            Request {selectedService}
          </Typography>
          
          <Typography variant="body2" color="text.secondary" textAlign="center" paragraph>
            Fill out the form below and we'll get back to you within 24 hours
          </Typography>

          <Divider sx={{ my: 3 }} />

          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              {/* Personal Information */}
              <Grid size={12}>
                <Typography variant="h6" gutterBottom>
                  Personal Information
                </Typography>
              </Grid>
              
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Full Name"
                  value={formData.name}
                  onChange={handleInputChange('name')}
                  required
                />
              </Grid>
              
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Phone Number"
                  value={formData.phone}
                  onChange={handleInputChange('phone')}
                  required
                  type="tel"
                />
              </Grid>
              
              
              <Grid size={12}>
                <TextField
                  fullWidth
                  label="Project Address"
                  value={formData.address}
                  onChange={handleInputChange('address')}
                  required
                  multiline
                  rows={2}
                />
              </Grid>

              {/* Project Details */}
              <Grid size={12}>
                <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                  Project Details
                </Typography>
              </Grid>
              
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControl fullWidth>
                  <InputLabel>Project Type</InputLabel>
                  <Select
                    value={formData.projectType}
                    onChange={handleInputChange('projectType')}
                    label="Project Type"
                  >
                    <MenuItem value="residential">Residential</MenuItem>
                    <MenuItem value="commercial">Commercial</MenuItem>
                    <MenuItem value="renovation">Renovation</MenuItem>
                    <MenuItem value="repair">Repair & Maintenance</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControl fullWidth>
                  <InputLabel>Budget Range</InputLabel>
                  <Select
                    value={formData.budget}
                    onChange={handleInputChange('budget')}
                    label="Budget Range"
                  >
                    <MenuItem value="under-1lakh">Under ₹1 Lakh</MenuItem>
                    <MenuItem value="1-5lakh">₹1-5 Lakhs</MenuItem>
                    <MenuItem value="5-10lakh">₹5-10 Lakhs</MenuItem>
                    <MenuItem value="10-25lakh">₹10-25 Lakhs</MenuItem>
                    <MenuItem value="25-50lakh">₹25-50 Lakhs</MenuItem>
                    <MenuItem value="above-50lakh">Above ₹50 Lakhs</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControl fullWidth>
                  <InputLabel>Timeline</InputLabel>
                  <Select
                    value={formData.timeline}
                    onChange={handleInputChange('timeline')}
                    label="Timeline"
                  >
                    <MenuItem value="asap">ASAP</MenuItem>
                    <MenuItem value="1month">Within 1 Month</MenuItem>
                    <MenuItem value="3months">Within 3 Months</MenuItem>
                    <MenuItem value="6months">Within 6 Months</MenuItem>
                    <MenuItem value="flexible">Flexible</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControl fullWidth>
                  <InputLabel>Urgency</InputLabel>
                  <Select
                    value={formData.urgency}
                    onChange={handleInputChange('urgency')}
                    label="Urgency"
                  >
                    <MenuItem value="low">Low</MenuItem>
                    <MenuItem value="medium">Medium</MenuItem>
                    <MenuItem value="high">High</MenuItem>
                    <MenuItem value="urgent">Urgent</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* Contact Preferences */}
              <Grid size={12}>
                <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                  Contact Preferences
                </Typography>
              </Grid>
              
              <Grid size={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.preferredContact.includes('phone')}
                      onChange={handleInputChange('preferredContact')}
                      value="phone"
                    />
                  }
                  label="Phone Call"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.preferredContact.includes('whatsapp')}
                      onChange={handleInputChange('preferredContact')}
                      value="whatsapp"
                    />
                  }
                  label="WhatsApp"
                />
              </Grid>

              {/* Additional Information */}
              <Grid size={12}>
                <TextField
                  fullWidth
                  label="Additional Requirements"
                  value={formData.additionalRequirements}
                  onChange={handleInputChange('additionalRequirements')}
                  multiline
                  rows={3}
                  placeholder="Please describe any specific requirements or special considerations..."
                />
              </Grid>
              
              <Grid size={12}>
                <TextField
                  fullWidth
                  label="Message"
                  value={formData.message}
                  onChange={handleInputChange('message')}
                  multiline
                  rows={3}
                  placeholder="Any additional information you'd like to share..."
                />
              </Grid>
            </Grid>

            {/* Submit Status */}
            {submitStatus === 'success' && (
              <Alert severity="success" sx={{ mt: 3 }}>
                Thank you! Your request has been submitted successfully. We'll contact you soon.
              </Alert>
            )}
            
            {submitStatus === 'error' && (
              <Alert severity="error" sx={{ mt: 3 }}>
                There was an error submitting your request. Please try again.
              </Alert>
            )}

            {/* Action Buttons */}
            <Box display="flex" gap={2} justifyContent="center" mt={4}>
              <Button
                variant="outlined"
                onClick={onClose}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={!isFormValid || isSubmitting}
                startIcon={isSubmitting ? <CircularProgress size={20} /> : <Send />}
                sx={{
                  background: 'linear-gradient(135deg, #1e40af 0%, #f59e0b 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #1e40afdd 0%, #f59e0bdd 100%)',
                  },
                }}
              >
                {isSubmitting ? 'Submitting...' : 'Submit Request'}
              </Button>
            </Box>
          </form>
        </CardContent>
      </Card>
    </motion.div>
  );
};

export default ServiceForm;
